import { Box, Center, HStack } from '@chakra-ui/layout'
import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text as ChakraText,
  useDisclosure,
} from '@chakra-ui/react'
import type { FormikHelpers } from 'formik'
import { useFormik } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import { SingleSignOnSettings } from '../../components/Settings'
import type {
  SingleSignOnData,
  SingleSignOnSettingsForm,
  SingleSignOnSettingsResponse,
  SingleSignOnSettingsErrorResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox } from '../Base/MainContent'
import useToast from '../../hooks/useToast'
import { FeaturesEnum } from '../../../utils/features'

const SingleSignOnSettingsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { isOpen, onClose, onOpen } = useDisclosure()

  const initialMetadataXML = {
    _id: '',
    metadataXML: '',
    publicKey: '',
    clientNumber: '',
    imagekitId: '',
    entityId: '',
  }

  const [newMetadataXML, setNewMetadataXML] = useState<boolean>(true)
  const [metadataXML, setMetadataXML] =
    useState<SingleSignOnData>(initialMetadataXML)

  const toast = useToast()

  const { request, loading } = useFetch<
    SingleSignOnSettingsResponse & SingleSignOnSettingsErrorResponse
  >({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (
      user?.clientNumber &&
      user.pricingPlan.featuresList.includes(FeaturesEnum.SINGLE_SIGN_ON)
    ) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/single-sign-on`,
      )

      if (!request.error && response[0]) {
        setNewMetadataXML(false)
        const { metadataXML, entityId } = response[0]
        setMetadataXML(response[0])
        form.resetForm({
          values: {
            metadataXML,
            entityId
          },
        })
      } else {
        setNewMetadataXML(true)
        setMetadataXML(initialMetadataXML)
        form.resetForm({
          values: {
            metadataXML: '',
            entityId: '',
          },
        })
      }
    }
  }

  const handleSubmit = async (
    values: SingleSignOnSettingsForm,
    _formikHelpers: FormikHelpers<SingleSignOnSettingsForm>,
  ): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.post(
        `/api/clients/${user.clientNumber}/single-sign-on`,
        values,
      )
      setSubmitting(true)
      if (request.error) {
        const apiErrorMessage = response.errors[0]?.msg?.reason?.message
        toast({
          description:
            apiErrorMessage ||
            t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      toast({
        description: t(
          'successfully_updated_metadata_xml',
          'Successfully updated metadata XML',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      await getData()
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (user?.clientNumber) {
      await request.delete(
        `/api/clients/${user.clientNumber}/single-sign-on/${metadataXML._id}`,
      )
      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      toast({
        description: t(
          'successfully_deleted_metadata_xml',
          'Successfully deleted metadata XML',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      await getData()
    }
  }

  const form = useFormik<SingleSignOnSettingsForm>({
    initialValues: {
      metadataXML: '',
      entityId: '',
    },
    validationSchema: Yup.object().shape({
      metadataXML: Yup.string()
        .min(1, 'Minimum length should be 1')
        .required(t('required', 'Required')),
      entityId: Yup.string()
        .url(
          t('entity_id_url_invalid',
            'Invalid URL. Enter a complete URL e.g. https://imagekit.io/saml/consume#your_imagekit_id'),
        )
    }),
    onSubmit: handleSubmit,
  })

  const { dirty, submitForm, isSubmitting, setSubmitting, isValid } = form

  useEffect(() => {
    void getData()
  }, [user])

  if (loading) {
    return (
      <Center height="100%">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  }

  return (
    <>
      {user?.pricingPlan.featuresList.includes(FeaturesEnum.SINGLE_SIGN_ON) ? (
        <MainContentHeadingBox reverse={true}>
          <HStack spacing="1em">
            <Button
              variant="deleteCTA"
              onClick={onOpen}
              isDisabled={newMetadataXML}
            >
              {t('delete')}
            </Button>
            <Button
              onClick={() => {
                void submitForm()
              }}
              isLoading={isSubmitting}
              isDisabled={!dirty || !isValid}
            >
              {t('save')}
            </Button>
          </HStack>
        </MainContentHeadingBox>
      ) : (
        <MainContentHeadingBox>
          <Stack direction={['column']} spacing={1}>
            <Heading
              as="h2"
              size="md"
              fontWeight="600"
              color={colors.appDarkIndigo}
            >
              {t('metadata_xml', 'Metadata XML')}
            </Heading>
            <ChakraText color="appBattleshipGrey">
              {t(
                'metadata_xml_description',
                'Enable SSO for your users by specifying a metadata XML configuration here',
              )}
            </ChakraText>
          </Stack>
        </MainContentHeadingBox>
      )}
      <Box padding="1.5em">
        <SingleSignOnSettings
          user={user}
          form={form}
          disabled={
            !user?.pricingPlan.featuresList.includes(
              FeaturesEnum.SINGLE_SIGN_ON,
            )
          }
        />
      </Box>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('delete_metadata_xml', 'Delete metadata XML')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ChakraText>
              {t(
                'delete_metadata_xml_description',
                'This will disable Single-Sign On.\n Save metadata XML again to re-enable it.',
              )}
            </ChakraText>
          </ModalBody>
          <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
            <Button onClick={onClose} variant="outline">
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              isLoading={form.isSubmitting}
              backgroundColor={colors.appWatermelon}
              borderColor={colors.appWatermelon}
              onClick={async () => {
                await handleDelete()
                onClose()
              }}
            >
              {t('submit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default SingleSignOnSettingsLayout
